/**
 * Brand logo mapping for fuel station markers
 * Contains real logo URLs and styling information for major UK fuel retailers
 */

export interface BrandLogoInfo {
  name: string;
  logoUrl?: string;
  svg?: string;
  backgroundColor: string;
  textColor: string;
  borderColor?: string;
}

/**
 * Real logo URLs and styling for major fuel station brands in the UK
 * Using publicly accessible brand assets where possible, with SVG fallbacks
 */
export const BRAND_LOGOS: Record<string, BrandLogoInfo> = {
  // BP - Green brand colors  
  'BP': {
    name: 'BP',
    logoUrl: '/logos/bp.svg',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="bpGrad" cx="0.5" cy="0.3">
          <stop offset="0%" style="stop-color:#00D554"/>
          <stop offset="100%" style="stop-color:#00A651"/>
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="45" fill="url(#bpGrad)" stroke="#ffffff" stroke-width="2"/>
      <text x="50" y="60" text-anchor="middle" fill="white" font-family="Helvetica,Arial,sans-serif" font-size="26" font-weight="900">BP</text>
    </svg>`,
    backgroundColor: '#00A651',
    textColor: '#ffffff',
    borderColor: '#ffffff'
  },

  // Shell - Yellow and red brand colors
  'SHELL': {
    name: 'Shell',
    logoUrl: '/logos/shell.svg',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="shellGrad" cx="0.5" cy="0.3">
          <stop offset="0%" style="stop-color:#FFED4E"/>
          <stop offset="100%" style="stop-color:#FFD100"/>
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="45" fill="url(#shellGrad)" stroke="#ffffff" stroke-width="2"/>
      <path d="M50 25 C42 25 35 30 30 38 C35 50 42 60 50 75 C58 60 65 50 70 38 C65 30 58 25 50 25 Z" fill="#E31E24"/>
      <path d="M50 30 C45 30 40 33 37 38 C40 45 45 52 50 65 C55 52 60 45 63 38 C60 33 55 30 50 30 Z" fill="#FFD100"/>
    </svg>`,
    backgroundColor: '#FFD100',
    textColor: '#E31E24',
    borderColor: '#ffffff'
  },

  'Shell': {
    name: 'Shell',
    logoUrl: '/logos/shell.svg',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="shellGrad2" cx="0.5" cy="0.3">
          <stop offset="0%" style="stop-color:#FFED4E"/>
          <stop offset="100%" style="stop-color:#FFD100"/>
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="45" fill="url(#shellGrad2)" stroke="#ffffff" stroke-width="2"/>
      <path d="M50 25 C42 25 35 30 30 38 C35 50 42 60 50 75 C58 60 65 50 70 38 C65 30 58 25 50 25 Z" fill="#E31E24"/>
      <path d="M50 30 C45 30 40 33 37 38 C40 45 45 52 50 65 C55 52 60 45 63 38 C60 33 55 30 50 30 Z" fill="#FFD100"/>
    </svg>`,
    backgroundColor: '#FFD100',
    textColor: '#E31E24',
    borderColor: '#ffffff'
  },

  // Esso - Blue and red brand colors
  'ESSO': {
    name: 'Esso',
    logoUrl: '/logos/esso.svg',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="essoGrad" cx="0.5" cy="0.3">
          <stop offset="0%" style="stop-color:#0044BB"/>
          <stop offset="100%" style="stop-color:#003087"/>
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="45" fill="url(#essoGrad)" stroke="#ffffff" stroke-width="2"/>
      <ellipse cx="50" cy="42" rx="30" ry="14" fill="#E31E24"/>
      <text x="50" y="48" text-anchor="middle" fill="white" font-family="Helvetica,Arial,sans-serif" font-size="14" font-weight="bold">ESSO</text>
    </svg>`,
    backgroundColor: '#003087',
    textColor: '#ffffff',
    borderColor: '#ffffff'
  },

  'Esso': {
    name: 'Esso',
    logoUrl: '/logos/esso.svg',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="essoGrad2" cx="0.5" cy="0.3">
          <stop offset="0%" style="stop-color:#0044BB"/>
          <stop offset="100%" style="stop-color:#003087"/>
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="45" fill="url(#essoGrad2)" stroke="#ffffff" stroke-width="2"/>
      <ellipse cx="50" cy="42" rx="30" ry="14" fill="#E31E24"/>
      <text x="50" y="48" text-anchor="middle" fill="white" font-family="Helvetica,Arial,sans-serif" font-size="14" font-weight="bold">ESSO</text>
    </svg>`,
    backgroundColor: '#003087',
    textColor: '#ffffff',
    borderColor: '#ffffff'
  },

  // Tesco - Blue and red dots brand colors
  'TESCO': {
    name: 'Tesco',
    logoUrl: '/logos/tesco.svg',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="tescoGrad" cx="0.5" cy="0.3">
          <stop offset="0%" style="stop-color:#0066DD"/>
          <stop offset="100%" style="stop-color:#0050AA"/>
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="45" fill="url(#tescoGrad)" stroke="#ffffff" stroke-width="2"/>
      <circle cx="37" cy="35" r="5" fill="#E31E24"/>
      <circle cx="63" cy="35" r="5" fill="#E31E24"/>
      <circle cx="50" cy="65" r="5" fill="#E31E24"/>
      <text x="50" y="53" text-anchor="middle" fill="white" font-family="Helvetica,Arial,sans-serif" font-size="13" font-weight="900">Tesco</text>
    </svg>`,
    backgroundColor: '#0050AA',
    textColor: '#ffffff',
    borderColor: '#ffffff'
  },

  // Tesco lowercase variant
  'tesco': {
    name: 'Tesco',
    logoUrl: '/logos/tesco.svg',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="tescoGrad2" cx="0.5" cy="0.3">
          <stop offset="0%" style="stop-color:#0066DD"/>
          <stop offset="100%" style="stop-color:#0050AA"/>
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="45" fill="url(#tescoGrad2)" stroke="#ffffff" stroke-width="2"/>
      <circle cx="37" cy="35" r="5" fill="#E31E24"/>
      <circle cx="63" cy="35" r="5" fill="#E31E24"/>
      <circle cx="50" cy="65" r="5" fill="#E31E24"/>
      <text x="50" y="53" text-anchor="middle" fill="white" font-family="Helvetica,Arial,sans-serif" font-size="13" font-weight="900">Tesco</text>
    </svg>`,
    backgroundColor: '#0050AA',
    textColor: '#ffffff',
    borderColor: '#ffffff'
  },

  // Sainsburys - Orange brand colors  
  'Sainsburys': {
    name: "Sainsbury's",
    logoUrl: '/logos/sainsburys.svg',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="45" fill="#FF8200"/>
      <text x="50" y="48" text-anchor="middle" fill="white" font-family="Helvetica,Arial,sans-serif" font-size="11" font-weight="bold">SAINSBURY'S</text>
      <text x="50" y="65" text-anchor="middle" fill="white" font-family="Helvetica,Arial,sans-serif" font-size="9">FUEL</text>
    </svg>`,
    backgroundColor: '#FF8200',
    textColor: '#ffffff',
    borderColor: '#ffffff'
  },

  // Sainsbury's with apostrophe variant
  "Sainsbury's": {
    name: "Sainsbury's",
    logoUrl: '/logos/sainsburys.svg',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="45" fill="#FF8200"/>
      <text x="50" y="48" text-anchor="middle" fill="white" font-family="Helvetica,Arial,sans-serif" font-size="11" font-weight="bold">SAINSBURY'S</text>
      <text x="50" y="65" text-anchor="middle" fill="white" font-family="Helvetica,Arial,sans-serif" font-size="9">FUEL</text>
    </svg>`,
    backgroundColor: '#FF8200',
    textColor: '#ffffff',
    borderColor: '#ffffff'
  },

  // Asda - Green brand colors
  'Asda': {
    name: 'Asda',
    logoUrl: '/logos/asda.png',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="45" fill="#00B140"/>
      <text x="50" y="58" text-anchor="middle" fill="white" font-family="Helvetica,Arial,sans-serif" font-size="20" font-weight="bold">ASDA</text>
    </svg>`,
    backgroundColor: '#00B140',
    textColor: '#ffffff',
    borderColor: '#ffffff'
  },

  // Asda Express - Same as Asda
  'Asda Express': {
    name: 'Asda Express',
    logoUrl: '/logos/asda.png',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="45" fill="#00B140"/>
      <text x="50" y="58" text-anchor="middle" fill="white" font-family="Helvetica,Arial,sans-serif" font-size="20" font-weight="bold">ASDA</text>
    </svg>`,
    backgroundColor: '#00B140',
    textColor: '#ffffff',
    borderColor: '#ffffff'
  },

  // Morrisons - Yellow and green
  'Morrisons': {
    name: 'Morrisons',
    logoUrl: '/logos/morrisons.svg',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="45" fill="#FFDD00"/>
      <text x="50" y="48" text-anchor="middle" fill="#004225" font-family="Helvetica,Arial,sans-serif" font-size="12" font-weight="bold">Morrisons</text>
      <text x="50" y="65" text-anchor="middle" fill="#004225" font-family="Helvetica,Arial,sans-serif" font-size="8">FUEL</text>
    </svg>`,
    backgroundColor: '#FFDD00',
    textColor: '#004225',
    borderColor: '#ffffff'
  },

  // Texaco - Red with star
  'Texaco': {
    name: 'Texaco',
    logoUrl: '/logos/texaco.png',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="45" fill="#E31E24"/>
      <polygon points="50,20 55,40 70,35 58,48 75,55 58,62 70,75 55,70 50,90 45,70 30,75 42,62 25,55 42,48 30,35 45,40" fill="white"/>
    </svg>`,
    backgroundColor: '#E31E24',
    textColor: '#ffffff',
    borderColor: '#ffffff'
  },

  // JET - Blue brand
  'JET': {
    name: 'JET',
    logoUrl: '/logos/jet.svg',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="45" fill="#0066CC"/>
      <text x="50" y="58" text-anchor="middle" fill="white" font-family="Helvetica,Arial,sans-serif" font-size="20" font-weight="bold">JET</text>
    </svg>`,
    backgroundColor: '#0066CC',
    textColor: '#ffffff',
    borderColor: '#ffffff'
  },

  // Motor Fuel Group - Generic fuel pump
  'Motor Fuel Group': {
    name: 'MFG',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="45" fill="#4A90E2"/>
      <text x="50" y="58" text-anchor="middle" fill="white" font-family="Helvetica,Arial,sans-serif" font-size="16" font-weight="bold">MFG</text>
    </svg>`,
    backgroundColor: '#4A90E2',
    textColor: '#ffffff',
    borderColor: '#ffffff'
  },

  // Murco - Red and blue brand colors
  'Murco': {
    name: 'Murco',
    logoUrl: '/logos/murco.jpg',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="45" fill="#E31E24"/>
      <text x="50" y="58" text-anchor="middle" fill="white" font-family="Helvetica,Arial,sans-serif" font-size="18" font-weight="bold">MURCO</text>
    </svg>`,
    backgroundColor: '#E31E24',
    textColor: '#ffffff',
    borderColor: '#ffffff'
  },

  // Co-op - Blue brand colors with clover leaf logo
  'Coop': {
    name: 'Co-op',
    logoUrl: '/logos/coop.svg',
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="45" fill="#00B5E2"/>
      <text x="50" y="58" text-anchor="middle" fill="white" font-family="Helvetica,Arial,sans-serif" font-size="16" font-weight="bold">Co-op</text>
    </svg>`,
    backgroundColor: '#00B5E2',
    textColor: '#ffffff',
    borderColor: '#ffffff'
  }
};

/**
 * Get brand logo information for a station brand
 */
export function getBrandLogo(brand: string): BrandLogoInfo {
  // Normalize brand name (handle case variations)
  const normalizedBrand = brand?.toUpperCase().trim();
  
  // Direct lookup
  if (BRAND_LOGOS[brand]) {
    return BRAND_LOGOS[brand];
  }
  
  // Case-insensitive lookup
  const matchingKey = Object.keys(BRAND_LOGOS).find(
    key => key.toUpperCase() === normalizedBrand
  );
  
  if (matchingKey) {
    return BRAND_LOGOS[matchingKey];
  }
  
  // Default fallback for unknown brands
  return {
    name: brand || 'Unknown',
    svg: `
      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="45" fill="#6B7280" stroke="#fff" stroke-width="3"/>
        <text x="50" y="58" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="16" font-weight="bold">⛽</text>
      </svg>
    `,
    backgroundColor: '#6B7280',
    textColor: '#ffffff',
    borderColor: '#ffffff'
  };
}

/**
 * Get the logo URL for a brand, preferring real logo URLs over SVG fallbacks
 */
export function getLogoUrl(logoInfo: BrandLogoInfo): string {
  // If we have a real logo URL, use it
  if (logoInfo.logoUrl) {
    return logoInfo.logoUrl;
  }
  
  // Otherwise, create a data URL from the SVG fallback
  if (logoInfo.svg) {
    const encodedSvg = encodeURIComponent(logoInfo.svg.trim());
    return `data:image/svg+xml,${encodedSvg}`;
  }
  
  // Last resort fallback
  return 'data:image/svg+xml,' + encodeURIComponent(`
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="45" fill="#6B7280"/>
      <text x="50" y="58" text-anchor="middle" fill="white" font-family="Arial,sans-serif" font-size="24">⛽</text>
    </svg>
  `);
}

/**
 * Create a data URL for a brand logo SVG (legacy function for backwards compatibility)
 */
export function createLogoDataUrl(logoInfo: BrandLogoInfo): string {
  return getLogoUrl(logoInfo);
}